import {
  OrganizationProfile,
  objectIdSchema,
  orgProfileUpdateSchema,
} from '@hero/schema';
import { z } from 'zod';
import { API } from '..';

/**
 * `org/profile` API contracts. Replaces the legacy `organization/profile`
 * surface (see `organization.api.ts`, slated for deletion in the same PR).
 *
 * **Request bodies are section-keyed and field-level partial.** See
 * `orgProfileUpdateSchema` for the contract — anything not present is
 * left untouched on the server.
 *
 * **Response shape stays `OrganizationProfile`** — the flat
 * `legal + bank + settings` projection composed by `OrgProfileMapperService`
 * from `Org + Bank`. Keeping the response flat means form initial-values
 * code on every consumer keeps working without a flatten step.
 */

// ─── current user ────────────────────────────────────────────────────

/** Empty body — server resolves the org from the auth context. */
export const orgProfileGetAPISchema = z.object({}).optional();
/** Get the current user's org profile. */
export type OrgProfileGetAPI = API<
  z.infer<typeof orgProfileGetAPISchema>,
  Partial<OrganizationProfile>
>;

/** Multi-section partial update body. */
export const orgProfileUpdateAPISchema = orgProfileUpdateSchema;
/**
 * Update the current user's org profile (partial).
 *
 * Response is `Partial<OrganizationProfile>` — same projection
 * shape as `OrgProfileGetAPI` — because an Org with no bank still
 * produces a partial map (missing `bankName` / `IBAN`). FE
 * consumers were already reading the get shape this way; the
 * update simply round-trips the post-write snapshot.
 */
export type OrgProfileUpdateAPI = API<
  z.infer<typeof orgProfileUpdateAPISchema>,
  Partial<OrganizationProfile>
>;

// ─── admin variants ──────────────────────────────────────────────────

/** Admin: load any org's profile by id. */
export const orgProfileAdminGetAPISchema = z.object({
  /** the org id (Org collection) to read. */
  orgId: objectIdSchema,
});
export type OrgProfileAdminGetAPI = API<
  z.infer<typeof orgProfileAdminGetAPISchema>,
  Partial<OrganizationProfile>
>;

/** Admin: partial update on any org by id. */
export const orgProfileAdminUpdateAPISchema = orgProfileUpdateSchema.extend(
  orgProfileAdminGetAPISchema.shape,
);
export type OrgProfileAdminUpdateAPI = API<
  z.infer<typeof orgProfileAdminUpdateAPISchema>,
  Partial<OrganizationProfile>
>;
